import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/utils/formatPrice";
import type { PlayerPriceResult } from "@/lib/playerPricing";
import { useTranslation } from "react-i18next";

interface PriceSummaryProps {
  playerPrices: PlayerPriceResult[];
  currentUserId: string;
  splitStrategy: "full" | "per_person";
  totalPrice: number;
  courtMaxPlayers: number;
  isActivity: boolean;
  addonsTotal?: number;
}

export default function PriceSummary({
  playerPrices,
  currentUserId,
  splitStrategy,
  totalPrice,
  courtMaxPlayers,
  isActivity,
  addonsTotal = 0,
}: PriceSummaryProps) {
  const { t } = useTranslation("booking");
  const payFullAmount = totalPrice + addonsTotal;
  const pricePerPerson = Math.ceil(payFullAmount / courtMaxPlayers);
  const confirmedPlayers = playerPrices.length;
  const emptySpots = Math.max(0, courtMaxPlayers - confirmedPlayers);

  const bookerPays = isActivity
    ? pricePerPerson
    : pricePerPerson + pricePerPerson * emptySpots;

  const myPrice = playerPrices.find((p) => p.userId === currentUserId);

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <p className="text-sm font-medium">{t("priceOverview")}</p>

      {/* Member discount callout */}
      {myPrice && myPrice.categorySlug !== "standard" && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {myPrice.categoryName}
          </Badge>
          {myPrice.dailyMinutesLimit != null && (
            <span className="text-[10px] text-muted-foreground">
              {t("usedToday", { used: myPrice.dailyMinutesUsedBefore, limit: myPrice.dailyMinutesLimit })}
            </span>
          )}
        </div>
      )}

      {/* Per-player breakdown */}
      {playerPrices.map((pp) => {
        const isBooker = pp.userId === currentUserId;
        const playerAmount =
          splitStrategy === "full"
            ? isBooker
              ? payFullAmount
              : 0
            : isBooker
              ? bookerPays
              : pricePerPerson;
        const spots =
          isBooker && !isActivity && splitStrategy === "per_person"
            ? `${1 + emptySpots} ${1 + emptySpots > 1 ? t("spots") : t("spot")}`
            : `1 ${t("spot")}`;
        return (
          <div key={pp.userId} className="flex justify-between text-xs">
            <span className="flex items-center gap-1.5">
              {isBooker ? t("you") : pp.categoryName}
              {pp.categorySlug !== "standard" && (
                <Badge variant="outline" className="text-[10px] py-0 px-1">
                  {pp.categoryName}
                </Badge>
              )}
              <span className="text-muted-foreground">({spots})</span>
            </span>
            <span className="font-medium">{formatPrice(playerAmount)}</span>
          </div>
        );
      })}

      {/* Open spots */}
      {isActivity && splitStrategy === "per_person" && emptySpots > 0 && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>
            {t("openSpots", { count: emptySpots, label: emptySpots > 1 ? t("spots") : t("spot") })}
          </span>
          <span>
            {formatPrice(pricePerPerson * emptySpots)} ({t("paidByJoiners")})
          </span>
        </div>
      )}

      {/* Total */}
      <div className="flex justify-between text-xs font-medium pt-1.5 border-t">
        <span>{t("total")}</span>
        <span>{formatPrice(payFullAmount)}</span>
      </div>
    </div>
  );
}
